import React, { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ProdutoNasajonItem, searchProdutosNasajon } from '../../../api/orcamentoCiliaApi';

/**
 * Campo "Produto no Nasajon" do detalhe da peça (13/10/2026, pedido do Victor).
 *
 * **O que havia antes, e por que foi trocado**: um input de código que só agia no blur/Enter —
 * digitar não produzia nada visível — e uma lupa ao lado que abria OUTRO modal de busca com o
 * campo em branco, obrigando a digitar de novo o que já tinha sido digitado. Dois caminhos para
 * a mesma pergunta ("qual produto do ERP é esta peça?"), nenhum dos dois respondendo enquanto a
 * pessoa escreve.
 *
 * Agora é UM campo: o que se digita é buscado ao vivo no cadastro do Nasajon (código ou
 * descrição, com atraso curto para não disparar uma consulta por tecla) e a lista aparece
 * embaixo, dentro do próprio modal. Escolher uma linha vincula. Não há mais "digite o código e
 * saia do campo": o vínculo é sempre uma escolha explícita numa lista que o ERP devolveu, o que
 * também elimina o código digitado errado que o backend recusava sem a tela explicar.
 *
 * 🔴 "Nada encontrado" só aparece quando a consulta RESPONDEU. Falha de rede mostra texto
 * próprio: lido como "não existe no cadastro", levaria a criar a peça em duplicata no envio
 * (regra da at-60).
 */

const ATRASO_DA_BUSCA_MS = 300;
const MINIMO_DE_CARACTERES = 2;

interface Props {
  /** Código do produto já vinculado (null = peça nova, será criada no envio). */
  codigoAtual: string | null;
  /** A peça está reconhecida no ERP — mostra o vínculo em vez do campo de busca. */
  vinculado: boolean;
  disabled: boolean;
  /**
   * O vínculo (ou o desfazer) está em curso no servidor. Demora: o backend revalida o código no
   * Nasajon real antes de gravar. Sem este sinal o campo voltava ao estado vazio e a pessoa
   * ficava segundos sem saber se o clique valeu (medido pelo Victor em 13/10/2026: ~10 s).
   */
  vinculando: boolean;
  onVincular: (produto: ProdutoNasajonItem) => void;
  onDesvincular: () => void;
}

const BuscaProdutoNasajon: React.FC<Props> = ({ codigoAtual, vinculado, disabled, vinculando, onVincular, onDesvincular }) => {
  const [termo, setTermo] = useState('');
  const [termoBuscado, setTermoBuscado] = useState('');
  const [trocando, setTrocando] = useState(false);
  const [destaque, setDestaque] = useState(0);
  /** O produto clicado, exibido enquanto o servidor responde. */
  const [escolhido, setEscolhido] = useState<ProdutoNasajonItem | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Servidor respondeu (sucesso ou falha): a escolha deixa de estar "em curso".
  useEffect(() => {
    if (!vinculando) setEscolhido(null);
  }, [vinculando]);

  // Debounce: a consulta sai só depois de a pessoa parar de digitar.
  useEffect(() => {
    const t = setTimeout(() => setTermoBuscado(termo.trim()), ATRASO_DA_BUSCA_MS);
    return () => clearTimeout(t);
  }, [termo]);

  // Vínculo refeito (ou desfeito) por fora: volta ao estado inicial.
  useEffect(() => {
    setTrocando(false);
    setTermo('');
  }, [codigoAtual, vinculado]);

  const buscando = !vinculado || trocando;
  const podeBuscar = buscando && termoBuscado.length >= MINIMO_DE_CARACTERES;

  const { data: produtos, isFetching, isError } = useQuery({
    queryKey: ['orcamento-cilia', 'produtos-nasajon', termoBuscado],
    queryFn: () => searchProdutosNasajon(termoBuscado),
    enabled: podeBuscar,
    retry: false,
    staleTime: 60_000,
  });

  useEffect(() => setDestaque(0), [produtos]);

  const lista = podeBuscar ? produtos ?? [] : [];
  const aguardandoDebounce = buscando && termo.trim().length >= MINIMO_DE_CARACTERES && termo.trim() !== termoBuscado;

  const escolher = (p: ProdutoNasajonItem) => {
    if (!p.codigo || disabled) return;
    setEscolhido(p);
    onVincular(p);
    setTermo('');
    setTrocando(false);
  };

  const aoTeclar = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown' && lista.length) {
      e.preventDefault();
      setDestaque((d) => Math.min(d + 1, lista.length - 1));
    } else if (e.key === 'ArrowUp' && lista.length) {
      e.preventDefault();
      setDestaque((d) => Math.max(d - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      // Código digitado por inteiro vence a posição da lista: é o caso de quem já sabe o código.
      const exato = lista.find((p) => p.codigo && p.codigo.trim() === termo.trim());
      const alvo = exato ?? lista[destaque];
      if (alvo) escolher(alvo);
    } else if (e.key === 'Escape' && (termo || trocando)) {
      // Limpa o campo em vez de fechar o modal inteiro (o hospedeiro escuta Escape no document).
      e.stopPropagation();
      setTermo('');
      setTrocando(false);
    }
  };

  if (vinculando) {
    return (
      <div className="busca-produto">
        <div className="busca-produto-vinculo busca-produto-vinculo--andamento" role="status">
          <span className="busca-produto-spinner" aria-hidden="true" />
          {escolhido ? (
            <span>
              Vinculando <span className="font-mono">{escolhido.codigo}</span> ({escolhido.especificacao}) no Nasajon...
            </span>
          ) : (
            <span>Desfazendo o vínculo no Nasajon...</span>
          )}
        </div>
      </div>
    );
  }

  if (vinculado && !trocando) {
    return (
      <div className="busca-produto">
        <div className="busca-produto-vinculo">
          <span className="font-mono">{codigoAtual}</span>
          {!disabled && (
            <>
              <button
                type="button"
                className="link-action"
                title="Vincular a outro produto do cadastro do Nasajon"
                onClick={() => {
                  setTrocando(true);
                  setTimeout(() => inputRef.current?.focus(), 0);
                }}
              >
                trocar
              </button>
              <button
                type="button"
                className="link-action subtle"
                title="Desfazer o vínculo com o produto do Nasajon (volta a ser criada como peça nova)"
                onClick={onDesvincular}
              >
                desfazer vínculo
              </button>
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="busca-produto">
      <input
        ref={inputRef}
        type="text"
        className="cilia-input"
        value={termo}
        placeholder="Digite o código ou a descrição da peça no Nasajon"
        onChange={(e) => setTermo(e.target.value)}
        onKeyDown={aoTeclar}
        disabled={disabled}
        aria-label="Produto no Nasajon"
        aria-autocomplete="list"
        autoComplete="off"
      />
      {termo.trim().length < MINIMO_DE_CARACTERES ? (
        <span className="busca-produto-hint">
          A lista vem do cadastro do Nasajon, em tempo real. Escolha um produto para vincular; sem vínculo, a peça é
          criada como nova no envio.
          {trocando ? ' Esc cancela a troca.' : ''}
        </span>
      ) : (
        <div className="busca-produto-lista" role="listbox">
          {(isFetching || aguardandoDebounce) && <span className="busca-produto-estado">Buscando no Nasajon...</span>}
          {!isFetching && !aguardandoDebounce && isError && (
            <span className="busca-produto-estado busca-produto-estado--erro">
              Não foi possível consultar o cadastro do Nasajon. Tente de novo; se persistir, acione a TI.
            </span>
          )}
          {!isFetching && !aguardandoDebounce && !isError && podeBuscar && lista.length === 0 && (
            <span className="busca-produto-estado">
              Nada encontrado no cadastro do Nasajon para &ldquo;{termoBuscado}&rdquo;.
            </span>
          )}
          {!isFetching &&
            !aguardandoDebounce &&
            !isError &&
            lista.map((p, i) => (
              <button
                key={p.nasajonId}
                type="button"
                role="option"
                aria-selected={i === destaque}
                className={`busca-produto-linha ${i === destaque ? 'destaque' : ''}`}
                disabled={disabled || !p.codigo}
                title={!p.codigo ? 'Produto sem código no ERP não pode ser vinculado — corrija o cadastro no Nasajon' : undefined}
                onMouseEnter={() => setDestaque(i)}
                onClick={() => escolher(p)}
              >
                <span className="font-mono">{p.codigo || '(sem código)'}</span>
                <span>{p.especificacao}</span>
                <span className="busca-produto-ncm">{p.ncm ? `NCM ${p.ncm}` : ''}</span>
              </button>
            ))}
        </div>
      )}
    </div>
  );
};

export default BuscaProdutoNasajon;
